import { memo, useCallback, useEffect, useRef } from 'react';

import { WorkspaceAgentSelectCheckboxChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/select/WorkspaceAgentSelectCheckboxChip';
import { WorkspaceAgentSelectOptionButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/select/WorkspaceAgentSelectOptionButton';
import { WorkspaceAgentSomethingElseField } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/select/WorkspaceAgentSomethingElseField';
import { WorkspaceAgentSomethingElseOptionInput } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/select/WorkspaceAgentSomethingElseOptionInput';
import { useAnyOptionFullWidth } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useAnyOptionFullWidth';
import { SOMETHING_ELSE_LABEL } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type WorkspaceAgentSelectChoiceBodyProps = {
  customText: string;
  isMulti: boolean;
  /** Escape in the Something else field, or the pick-one X, unchecks it and clears the text. */
  onCustomDismiss: () => void;
  onCustomTextChange: (value: string) => void;
  onOptionClick: (index: number) => void;
  options: readonly string[];
  /** Pick one: what the Something else option reads. Callers always pass it; pick several passes undefined. */
  otherLabel: string | undefined;
  selectedIndexes: readonly number[];
};

/**
 * Pick one shows numbered options that hug their label and wrap long ones; Something else turns into a
 * field in its own slot. Pick several shows checkbox chips with the Something else field under them.
 * The dock shell owns the question, Cancel, and Continue.
 */
export const WorkspaceAgentSelectChoiceBody = memo(
  ({
    customText,
    isMulti,
    onCustomDismiss,
    onCustomTextChange,
    onOptionClick,
    options,
    otherLabel,
    selectedIndexes,
  }: WorkspaceAgentSelectChoiceBodyProps) => {
    const somethingElseIndex = options.length - 1;
    const somethingElseLabel = otherLabel?.trim() || SOMETHING_ELSE_LABEL;
    const choseCustom = selectedIndexes.includes(somethingElseIndex);
    const optionsRef = useRef<HTMLDivElement>(null);
    const anyOptionFullWidth = useAnyOptionFullWidth(optionsRef, !isMulti);
    const refocusSomethingElse = useRef<boolean>();
    const wasChoseCustom = useRef(choseCustom);

    // Leaving the field puts focus back inside the dock. Escape and the pick-one X return to the
    // Something else chip. A pointer click must not: the chip refuses focus so the next Enter continues,
    // and without this the unmounted field would drop focus on body, where the dock ignores keys.
    useEffect(() => {
      const leftCustom = wasChoseCustom.current && !choseCustom;
      wasChoseCustom.current = choseCustom;

      if (!leftCustom) {
        return;
      }

      if (refocusSomethingElse.current) {
        refocusSomethingElse.current = false;
        optionsRef.current
          ?.querySelector<HTMLElement>(`[data-option-index="${somethingElseIndex}"]`)
          ?.focus({ preventScroll: true });
        return;
      }

      const dock = optionsRef.current?.closest('section');

      if (dock instanceof HTMLElement && !dock.contains(document.activeElement)) {
        dock.focus({ preventScroll: true });
      }
    }, [choseCustom, somethingElseIndex]);

    const handleCustomDismiss = useCallback(() => {
      refocusSomethingElse.current = true;
      onCustomDismiss();
    }, [onCustomDismiss]);

    if (isMulti) {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-1.5" ref={optionsRef}>
            {options.map((label, index) => (
              <WorkspaceAgentSelectCheckboxChip
                index={index}
                key={label}
                label={label}
                onOptionClick={onOptionClick}
                selected={selectedIndexes.includes(index)}
              />
            ))}
          </div>
          {choseCustom ? (
            <WorkspaceAgentSomethingElseField
              onChange={onCustomTextChange}
              onDismiss={handleCustomDismiss}
              value={customText}
            />
          ) : null}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-1.5" ref={optionsRef}>
        {options.map((label, index) =>
          index === somethingElseIndex && choseCustom ? (
            <WorkspaceAgentSomethingElseOptionInput
              index={index}
              key={label}
              label={somethingElseLabel}
              onBack={handleCustomDismiss}
              onChange={onCustomTextChange}
              value={customText}
            />
          ) : (
            <WorkspaceAgentSelectOptionButton
              fullWidth={index === somethingElseIndex && anyOptionFullWidth}
              index={index}
              key={label}
              label={index === somethingElseIndex ? somethingElseLabel : label}
              onOptionClick={onOptionClick}
              selected={selectedIndexes.includes(index)}
            />
          ),
        )}
      </div>
    );
  },
);

WorkspaceAgentSelectChoiceBody.displayName = 'WorkspaceAgentSelectChoiceBody';
